import { SubAPI } from "./SubAPI";
import {Paginated} from "./Users";
import {DateString} from "./Projects";
import {Cents} from "nate-react-api-helpers";
import {Moment} from "moment";
import {TDiv10Type} from "../pages/project/shopdrawing/div10/Div10Tabs";
import {Div10Category, Div10Spec, PriceLine2} from "./Pricing";
import {GlassSpec} from "./QuotePricing";
import {DescriptionInput} from "../pages/logistics/purchasing/toSource/SubmitButton";
import {DoorSpec, FrameSpec, Preps, PrepsEnriched, ProductReviewStatus, WoodDetail} from "./Products";

export class Logistics extends SubAPI {

    listInventoryRequest(input: {
        project: number
        opening?: number
        offset?: number;
        state?: string;
        kind?: string; // e.g. hardware
        category?: "opening" | "div10" | "door-hm" | "frame-hm" | "door-wd" | "hardware" | "frame-anchor" | "component" | "glazing"
        nonStockingForRelease?: number;
        forReturn?: "true"
        forFreeform?: "true"
        notLinkedToOpening?: "true",
    }) {
        return this.fetcher.get<Paginated<InventoryRequest>>("/api/logistics/inventory-requests", input)
    }

    updateInventoryRequest(input: InventoryRequest) {
        return this.fetcher.post("/api/logistics/inventory-request", input)
    }

    sendDuplicate(input: {
        ids: number[];
        project: number;
    }) {
        return this.fetcher.post("/api/logistics/inventory-request/send-duplicate", input)
    }

    bulkUpdateInventoryRequest(input: {
        ids: number[];
        overrideSource?: InvSource;
    }) {
        return this.fetcher.post<{
            skippedForStockLimits: number[]
        }>("/api/logistics/inventory-request/bulk", input)
    }

    moveInventoryRequestsToPurchase(input: {
        ids: number[];
    }) {
        return this.fetcher.post<{
            moved: number;
            skipped: number;
        }>("/api/logistics/inventory-request/move-to-purchase", input)
    }

    setInventoryStocking(input: {
        product: number;
        stocking: boolean;
    }) {
        return this.fetcher.post("/api/logistics/inventory-item/set-stocking", input)
    }

    bulkUpdateRequestDueDates(input: {
        ids: number[],
        due: Moment|null,
    }) {
        return this.fetcher.post("/api/logistics/inventory-requests/bulk-update-due-date", input)
    }

    removeOrphanedInventoryRequests(input: {
        project: number;
        ids: number[];
        note: string;
    }) {
        return this.fetcher.post<{
            removed: number[];
            skipped: number[];
        }>("/api/logistics/inventory-requests/remove-orphaned", input)
    }

    archiveInventoryRequests(input: {
        project: number;
        ids: number[];
        note: string;
    }) {
        return this.fetcher.post<{
            archived: number[];
            skipped: number[];
        }>("/api/logistics/inventory-requests/archive", input)
    }

    listPurchaseOrders(input: {
        offset?: number;
        search?: string;
        project?: number;
        supplier?: number;
        outstanding?: boolean;
        forRouting?: boolean;
        readyForPickup?: boolean;
        readyForDelivery?: boolean;
        forConvertFromRouteStopGroup?: number;
        forPayables?: boolean;
        forDoorManufacture?: boolean
        includeInActive?: boolean;
    }) {
        return this.fetcher.get<Paginated<PurchaseOrder>>("/api/logistics/purchase-orders", input)
    }

    getPO(input: {id: number}) {
        return this.fetcher.get<PurchaseOrder>("/api/logistics/purchase-order", input)
    }

    updatePurchaseOrder(input: PurchaseOrder) {
        return this.fetcher.post<PurchaseOrder>("/api/logistics/purchase-order", input)
    }

    listPurchaseOrderItems(input: {
        offset?: number;
        purchaseOrder: number;
    }) {
        return this.fetcher.get<Paginated<PurchaseOrderItem>>("/api/logistics/purchase-order/items", input)
    }

    updatePurchaseOrderItem(input: Omit<PurchaseOrderItem, "logisticsQtyOutstanding">) {
        return this.fetcher.post("/api/logistics/purchase-order/item", input)
    }

    updatePurchaseOrderItemBulk(input: Omit<PurchaseOrderItem, "logisticsQtyOutstanding">[]) {
        return this.fetcher.post("/api/logistics/purchase-order/bulk-upsert", input)
    }

    listToSourceItems(props: {
        offset?: number;
        supplier?: number;
        type?: "hardware"|"door"|"frame"|"div10"|"frame-anchor"|"component"|"misc"|"glazing"
        subType?: TDiv10Type;
        sourceMode?: SourceMode;
        distributionMode?: DistributionMode
        project?: number;
    }) {
        return this.fetcher.get<Paginated<ToSourceItem>>("/api/logistics/items-to-source", props)
    }

    listInventory(props: {
        offset?: number;
        search?: string;
        kind?: string;
        subKind?: "hardware" | "door" | "frame" | "div10"| "component" | "frame-anchor" | "glazing"
        expandGroup?: number | null;
        expandAllGroups?: boolean;
    }) {
        return this.fetcher.get<Paginated<InventoryItem>>("/api/logistics/inventory-items", props)
    }

    listProjectInventory(props: {
        offset?: number;
        search?: string;
        subKind?: "hardware" | "door" | "frame" | "div10" | "component" | "glazing"
    }) {
        return this.fetcher.get<Paginated<{id: number} & InventoryItem>>("/api/logistics/project-inventory-items", props)
    }

    nonStockingInventoryItems(props: {
        offset?: number;
        search?: string;
        subKind?: "hardware" | "door" | "frame" | "div10" | "component" | "glazing"
    }) {
        return this.fetcher.get<Paginated<{id: number} & InventoryItem>>("/api/logistics/non-stocking-inventory-items", props)
    }

    updateInventory(props: InventoryItem) {
        return this.fetcher.post<InventoryItem>("/api/logistics/inventory-item", props)
    }

    relabelInventory(props: {
        product: number;
        targetProduct: number;
        targetDimHeight?: string;
        targetDimWidth?: string;
        targetDimLength?: string;
        targetHanding?: string;
        targetScriptInputs?: { name: string; value: string }[];
        qty: number;
        project?: number;
        opening?: number;
        isStocking: boolean;
        reorderRelated: boolean;
    }) {
        return this.fetcher.post("/api/logistics/inventory-item/relabel", props)
    }

    listRelabelRelated(props: {
        product: number;
        project?: number;
        opening?: number;
        isStocking: boolean;
    }) {
        return this.fetcher.get<RelabelRelatedRow[]>("/api/logistics/inventory-item/relabel/preview", props)
    }

    listInventoryToRestock(props: {
        offset?: number;
        search?: string;
        belowMin?: boolean;
    }) {
        return this.fetcher.get<Paginated<InventoryItem>>("/api/logistics/inventory-items/restocking", props)
    }

    dashSummary() {
        return this.fetcher.get<DashSummary>("/api/logistics/dash-summary", {})
    }

    updateRouteCalendar(input: {
        srcStop: number;
        dstRoute?: number;
        dstCreateRouteForDate?: string;
        dstSortOrder?: number;
    }) {
        return this.fetcher.post("/api/logistics/route/update-calendar", input)
    }

    updateRoute(props: DriverRoute) {
        return this.fetcher.post<DriverRoute>("/api/logistics/route", props)
    }

    driverUpdateRoute(props: DriverRoute) {
        return this.fetcher.post<DriverRoute>("/api/logistics/route/for-driver", props)
    }

    listRoutes(props: {
        active?: boolean;
        offset?: number;
        search?: string;
    }) {
        return this.fetcher.get<Paginated<DriverRoute>>("/api/logistics/routes", props)
    }

    getRoutesForWeek(props: {
        weekOf: string;
    }) {
        return this.fetcher.get<DriverRoute[]>("/api/logistics/routes-for-week", props)
    }

    getRoute(props: {id: number}) {
        return this.fetcher.get<DriverRoute>("/api/logistics/route", props)
    }

    optimizeRoute(props: {route: number}) {
        return this.fetcher.post<{
            beforeDurationSec: number;
            afterDurationSec: number;
            stops: RouteStop[];
        }>("/api/logistics/route/optimize", props)
    }

    optimizeRouteComplete(props: {
        route: number;
        stops: RouteStop[];
    }) {
        return this.fetcher.post("/api/logistics/route/optimize-complete", props)
    }

    listRouteStops(props: {
        route: number
    }) {
        return this.fetcher.get<RouteStop[]>("/api/logistics/route/stops", props)
    }

    upsertStop(input: RouteStop) {
        return this.fetcher.post("/api/logistics/route/stop", input)
    }

    stopViewedByDriver(input: {id: number}) {
        return this.fetcher.post("/api/logistics/route/stop/viewed-by-driver", input)
    }

    addRouteItems(input: {
        route: number;
        items: {
            pickupPo?: number | null;
            pickupDelivery?: number | null;
            dropProject?: number | null;
            stop?: number | null;
            manufacturer?: number | null;
            customStop?: string;
            customGroups?: string[];
            customStopAddressStreet?: string;
            customStopAddressCity?: string;
            customStopAddressPostal?: string;
            customGroupsFromDriver?: boolean;
        }[]
    }) {
        return this.fetcher.post("/api/logistics/route/add-items", input)
    }

    driverAddRouteItems(input: {
        route: number;
        driverVersion?: number;
        items: {
            pickupPo?: number | null;
            pickupDelivery?: number | null;
            dropProject?: number | null;
            stop?: number | null;
            manufacturer?: number | null;
            customStop?: string;
            customGroups?: string[];
            customStopAddressStreet?: string;
            customStopAddressCity?: string;
            customStopAddressPostal?: string;
            customGroupsFromDriver?: boolean;
        }[]
    }) {
        return this.fetcher.post("/api/logistics/route/add-items-for-driver", input)
    }

    listRouteAudit(input: {
        offset?: number;
        route?: number;
        group?: number;
    }) {
        return this.fetcher.get<Paginated<RouteAuditItem>>("/api/logistics/route/audit-log", input)
    }

    listRoutableItems(input: {
        offset?: number;
        type: "pickup-at-site" | "pickup-at-supplier" | "drop"
    }) {
        return this.fetcher.get<Paginated<DriverRoutePendingItem>>("/api/logistics/route/items-to-move", input)
    }

    reRouteGroup(input: {
        pickupGroupId: number;
        purchaseOrderItemsToWarehouse: number[]
    }) {
        return this.fetcher.post("/api/logistics/route/re-route-group", input)
    }

    upsertStopGroup(input: RouteStopGroup) {
        return this.fetcher.post("/api/logistics/route/stop-group", input)
    }

    driverUpsertStopGroupItems(input: {
        group: number;
        items: RouteStopGroupItem[];
        driverVersion?: number;
    }) {
        return this.fetcher.post("/api/logistics/route/stop-group-items-for-driver", input)
    }

    listSplitRouteItemsToSkip(input: {
        atGroup: number;
    }) {
        return this.fetcher.get<RouteStopGroupItem[]>("/api/logistics/route/split-route-items-to-skip", input)
    }

    sendRouteMessage(input: {
        route: number;
        content: string;
    }) {
        return this.fetcher.post("/api/logistics/route/messages/send", input)
    }

    getRouteMessages(input: {
        route: number;
    }) {
        return this.fetcher.get<RouteMessage[]>("/api/logistics/route/messages", input)
    }

    listDamagedProducts(props: {
        includeReviewed: boolean;
        project?: number;
        offset?: number;
    }) {
        return this.fetcher.get<Paginated<DamageReport>>("/api/logistics/damaged-products", props);
    }

    updateDamageReport(input: DamageReport) {
        return this.fetcher.post<{}>("/api/logistics/damaged-product", input);
    }

    listWasteProducts(props: {
        includeReviewed: boolean;
        project?: number;
        offset?: number;
    }) {
        return this.fetcher.get<Paginated<WasteReport>>("/api/logistics/waste/list", props);
    }

    updateWasteReport(props: WasteReport) {
        return this.fetcher.post<{}>("/api/logistics/waste", props);
    }

    listStockCounts(props: {
        offset?: number;
    }) {
        return this.fetcher.get<Paginated<StockCount>>("/api/logistics/stock-counts", props)
    }

    upsertStockCount(props: StockCount) {
        return this.fetcher.post<StockCount>("/api/logistics/stock-count", props)
    }

    startStockCount(props: {}) {
        return this.fetcher.post<{id: number}>("/api/logistics/stock-count/start", props)
    }

    completeStockCount(props: {
        id: number;
    }) {
        return this.fetcher.post("/api/logistics/stock-count/complete", props)
    }

    listStockCountItems(props: {
        offset?: number;
        search?: string;
        stockCount: number;
        kind?: "hardware" | "door" | "frame" | "div10"| "component" | "frame-anchor" | "glazing"
    }) {
        return this.fetcher.get<Paginated<StockCountItem>>("/api/logistics/stock-count/items", props)
    }

    updateStockCountItem(props: StockCountItem) {
        return this.fetcher.post("/api/logistics/stock-count/item", props)
    }

    upsertStockOrder(input: StockOrder) {
        return this.fetcher.post<StockOrder>("/api/logistics/stock-order", input)
    }

    listStockOrders(props: {
        offset?: number;
    }) {
        return this.fetcher.get<Paginated<StockOrder>>("/api/logistics/stock-orders", props)
    }

    startStockOrder(props: {}) {
        return this.fetcher.post<{id: number}>("/api/logistics/stock-order/start", props)
    }

    completeStockOrder(props: {
        id: number;
    }) {
        return this.fetcher.post("/api/logistics/stock-order/complete", props)
    }

    listStockOrderItems(props: {
        offset?: number;
        search?: string;
        stockOrder: number;
        kind?: "hardware" | "door" | "frame" | "div10"| "component" | "frame-anchor" | "glazing"
    }) {
        return this.fetcher.get<Paginated<StockOrderItem>>("/api/logistics/stock-order/items", props)
    }

    updateStockOrderItem(props: StockOrderItem) {
        return this.fetcher.post("/api/logistics/stock-order/item", props)
    }

    logGetSummary(props: {
        inventoryRequest?: number;
        miscProduct?: number;
        opening?: number;
    }) {
        return this.fetcher.get<LogisticsSummary[]>("/api/logistics/inventory-request-log/summary", props)
    }

    getInventoryRequestOrigin(props: {
        id: number;
    }) {
        return this.fetcher.get<InventoryRequestOrigin>("/api/logistics/inventory-request/origin", props)
    }

    logGetItems(props: {
        inventoryRequest?: number;
        miscProduct?: number;
        opening?: number;
        offset?: number;
    }) {
        return this.fetcher.get<Paginated<LogisticsLogItem>>("/api/logistics/inventory-request-log/list", props)
    }

    getReservations(props: {
        inventoryItem: number;
    }) {
        return this.fetcher.get<Paginated<InventoryReservation>>("/api/logistics/inventory-reservations", props)
    }

    getUpcomingReservations(props: {
        inventoryItem: number;
    }) {
        return this.fetcher.get<Paginated<PendingInventoryReservation>>("/api/logistics/inventory-reservations/pending", props)
    }

    removeReservation(props: {
        id: number;
        recalculate?: boolean;
    }) {
        return this.fetcher.post<{}>("/api/logistics/inventory-reservation/remove", props)
    }

    getDriverRouteInfo(props: {
        route: number;
    }) {
        return this.fetcher.get<{
            route: DriverRoute;
            stops: RouteStop[];
        }>("/api/logistics/route/driver-route-info", props);
    }
}

export type PendingInventoryReservation = {
    id: number;
    qty: number;
    project: number | null;
    projectName: string | null;
    projectNumber: number | null;
    openingName: string | null;
    floor: string | null;
    phase: string | null;
    neededBy: DateString | null;
}

export type InventoryReservation = {
    id: number;
    archived: boolean;
    inventoryItem: number;
    inventoryRequest: number | null;
    manufactureLog: number | null;
    qty: number;
    wantQty: number;
    updatedAt: DateString;
    project: number | null;
    projectName: string | null;
    projectNumber: number | null;
    openingName: string | null;
    floor: string | null;
    phase: string | null;
    neededBy: DateString | null;
    manufacturePo: number | null;
    manufacturePoNumber: string | null;
}

export type RouteMessage = {
    id: number;
    route: number;
    createdAt: DateString;
    createdBy: number;
    createdByName: string;
    content: string;
    viewedAt: DateString | null;
}

export type DriverRoute = {
    id: number;
    label: string;
    createdAt: DateString;
    date: DateString;
    completed: boolean;
    archived: boolean;

    driverVersion: number;

    stops?: RouteStop[]
}

export type RouteStop = {
    id: number;
    manufacturer?: number;
    manufacturerName?: string;

    project?: number;
    projectName?: string;
    projectNumber?: number;
    projectUseAltAddress?: number;
    projectUseAltAddressName?: string;
    projectUseContractorAddress?: number;

    route: number;
    customTextStop?: string;
    sortOrder: number;
    tykelWarehouse: boolean;

    contacts: RouteContact[];
    addressStreet: string | null;
    addressCity: string | null;
    addressPostal: string | null;
    addressProvince: string | null;

    // for custom stops
    customTextStopAddressStreet: string | null;
    customTextStopAddressCity: string | null;
    customTextStopAddressPostal: string | null;
    customCompanyForContacts: number | null;

    noteToDriver: string;

    driverViewed: boolean;
    driverAt?: boolean;

    groups: RouteStopGroup[] | null
}

export type RouteContact = {
    id: number; // from "contact" database table
    name: string;
    title: string;
    email: string;
    phone: string;
}

export type RouteStopGroup = {
    id: number;
    name: string;
    pickupPoNumber: string | null;
    pickupPurchaseOrder?: number;
    dropoffPoNumber: string | null;
    dropoffPurchaseOrder?: number;
    delivery?: number;
    deliveryProjectName?: string;
    deliveryProjectNumber?: number;

    deliveryReceiptBy: string;
    deliveryReceiptDoc?: number;
    deliveryReceiptTimestamp?: DateString;

    deliveryProofCompletedAt?: DateString;
    deliveryProofCompletedBy?: number;
    deliveryProofCount?: number;

    customTextGroup?: string;
    freeformChange?: number;
    routeStop: number;
    archived: boolean;
    completed: boolean;

    items: RouteStopGroupItem[] | null
}

export type RouteStopGroupItem = {
    id: number;
    name: string;
    qty: number;
    qtyReceived: number | null;
    qtyDamaged: number | null;
    routeStopGroup: number;
    uom: string;
    archived: boolean;

    purchaseOrderItem?: number;
    deliveryItem?: number;
    freeformChangeItemProduct?: number;
    deliveryBox?: number;
    dropoffAtSiteInNumStops?: number;

    openingMeta?: OpeningMeta[];
    boxDamageDetail?: Partial<RouteStopGroupItem>[];
}

export type OpeningMeta = {
    name: string
    seq: number
}

export type ProductPreview = {
    name: string;
    qty: number;

    qtyReceived?: number | null;
    qtyDamaged?: number | null;
    openingMeta?: OpeningMeta[];

    boxId?: number;
    boxLabel?: string;
    boxType?: string;
}

export type RouteAuditItem = {
    id: number;
    updatedByName: string;
    updatedAt: DateString;
    route: number;
    description: string;
}

export type DriverRoutePendingItem = {
    id: number;
    pickupPo: number | null;
    pickupPoNumber: string | null;
    pickupCompany: number | null;
    pickupCompanyName: string | null;
    pickupDelivery: number | null;
    dropProject: number | null;
    dropProjectName: string | null;
    dropProjectNumber: number | null;
    pickupProject: number | null;
    pickupProjectName: string | null;
    pickupProjectNumber: number | null;
    pickupFreeformChange: number | null;
    qty: number;
    contents: {
        name: string,
        qty: number,
        boxId?: number;
        boxLabel?: string;
        boxType?: string;
    }[];
    archived: boolean;
}

export type InventoryRequestOrigin = {
    changeset: {
        id: number;
        projSeqNumber: number;
        name: string;
        status: string;
    } | null;

    freeformChange: {
        id: number;
        name: string;
        status: string;
    } | null;

    removedByChangeset: {
        id: number;
        projSeqNumber: number;
        name: string;
        status: string;
    } | null;
}

export type LogisticsSummary = {
    lastUpdated: DateString;

    po: {
        id: number;
        poNumber: string;
        status: string;
    };

    delivery: {
        id: number;
        name: string;
        status: string;
        projectDeliveryNum?: string;
    };

    route: {
        id: number;
        name: string;
        completed: boolean;
    };

    freeformChange: {
        id: number;
        name: string;
        status: string;
    }
}

export type LogisticsLogItem = {
    id: number;
    inventoryRequest: number;
    opening: number;
    createdAt: DateString;
    createdBy: number;
    createdByName: string;
    description: string;
    purchaseOrder: number | null;
    poNumber: string | null;
    delivery: number | null;
    route: number | null;
    status: string
}

export type StockCount = {
    id: number;
    createdBy: number;
    createdByName: string;
    completed: boolean;
    updatedAt: DateString;
    archived: boolean;
    createdAt: DateString;

    qtyChanged: number;
    qtyChecked: number;
    qtySkipped: number;
    lossCents: Cents;
    gainCents: Cents;
}

export type StockCountItem = {
    id: number;
    product: number;

    kind: "hardware" | "door" | "frame" | "div10"| "component" | "frame-anchor" | "glazing";
    description: string;

    productName: string;
    productCode: string;
    finishCode: string;

    dimensions: string;
    glass?: GlassSpec;
    perLengthInventoryIncrement: string | null;
    perLengthPurchaseIncrement: string;

    inventoryItem: number;
    manufacturer: number;
    manufacturerName: string;

    qtyCounted: number;
    qtyExpected: number;
    note: string;
    checked: boolean;
    updatedAt: DateString;
    stockCount: number;

    changeCostCents: Cents;

    qty: number;
    stockMinQty: number;
    idealQty: number;
    warnReorder: boolean;
    warnLow: boolean;
}

export type StockOrder = {
    id: number;
    createdBy: number;
    createdByName: string;
    completed: boolean;
    updatedAt: DateString;
    archived: boolean;
    createdAt: DateString;

    qtyChecked: number;
    qtySkipped: number;
}

export type StockOrderItem = {
    id: number;

    kind: "hardware" | "door" | "frame" | "div-10" | "component" | "frame-anchor" | "glazing";
    description: string;
    product: number;
    baseProduct?: number;

    overrideSupplier?: number;

    productName: string;
    productCode: string;

    dimensions: string;
    glass?: GlassSpec;
    perLengthInventoryIncrement: string | null;
    perLengthPurchaseIncrement: string;

    inventoryItem: number;
    manufacturer: number;
    manufacturerName: string;

    qtyIdeal: number;
    qtyMinStock: number;
    qtyNeeded: number;
    qtyPendingPo: number;
    qtyReserved: number;
    qtyStock: number;
    qtyToOrder: number;

    note: string;
    checked: boolean;
    updatedAt: DateString;
    stockCount: number;
}

export type DistributionMode = "all" | "focused"

export type WasteReport = {
    id: number;
    productName: string;
    manufacturerName: string;
    logisticsDirective: string; // "warehouse" | "leave-at-supplier"

    reviewed: boolean;

    supplier: number | null;
    supplierName: string | null;
    poNumber: string | null;
    purchaseOrder: number | null;

    createdAt:     DateString;
    createdByName: string
    createdBy:     number;

    updatedAt: DateString;
    updatedBy: number;

    archived: boolean;

    qty: number;
    unitCostCents: number;
    markupCents: number | null;
    markupPercent: number | null;
    extendedPrice: number;

    note: string;
}

export type SourceMode = "to-order" | "stock"

export type DamageReport = {
    id: number;

    createdAt:     DateString;
    createdBy:     number;
    createdByName: string

    updatedAt: DateString;

    product:             number;
    productName:         string
    manufacturerName:    string
    qty:                 number;
    receivedUnitPrice:   Cents;
    purchaseOrder:       number;
    purchaseOrderNumber: string
    supplier: string;

    project?: number;

    wePayPercent?: number | null;
    wePayPerUnitCents:   number;
    theyPayPerUnitCents: number;
    reviewed:     boolean;
    usableQty?:    number;

    archived: boolean;
}

type DashSummary = {
    qtyToOrder: number;
    activeOrderCount: number;
    qtyToRestock: number;
    centsOrdered7d: number;
    centsOrdered7dPrev: number;
    centsPicked7d: number;
    centsPicked7dPrev: number;
    centsDelivered7d: number;
    centsDelivered7dPrev: number;
}

export type ToDeliverItem = {
    id: number;
    source: string;

    product: number;
    name:             string;
    productCode:      string;
    dimensions:       string;

    doorSeries?: string;
    doorMaterial?: string;
    doorFinish?: string;
    doorGauge?: string;
    doorElevation?: string;
    doorCore?: string;
    doorSeam?: string;
    doorHanding?: string;
    doorLabel?: string;

    frameSeries?: string;
    frameMaterial?: string;
    frameGauge?: string;
    frameScreenElev?: string;
    frameProfile?: string;
    frameJambDepth?: string;
    frameDoorThickness?: string;
    frameConstruction?: string;
    frameHanding?: string;
    frameHardwarePreps?: string;
    frameLabel?: string;
    frameType?: string;

    qty: number;

    manufacturer: number;
    manufacturerName: string;

    project: number;
    projectName: string;
    projectNumber: number;

    doorName?: string;

    inventoryRequestIds: number[];

    description?: string;
    dimWidth?: string;
    dimHeight?: string;
    dimLength?: string;
    finish?: string;
    div10CornerGuardFinish?: string;
    div10CornerGuardType?: string;
    div10Description?: string;
    div10LockerBaseTrim?: string;
    div10LockerCoating?: string;
    div10LockerEndPanels?: boolean;
    div10LockerSloped?: boolean;
    div10LockerTiers?: string;
    div10MailboxLoading?: string;
    div10MailboxMounting?: string;
    div10MailboxParcelUnits?: number;
    div10MailboxTenantUnits?: number;
    div10Note?: string;
    div10PartitionCoating?: string;
    div10Room?: string;
}

export type InventoryItem = {
    id: number;
    phase?: string | null;
    qty: number;
    stockMinQty: number;
    idealQty: number;
    isStocking: boolean;
    archived: boolean;

    project: number;
    projectName: string;
    projectNumber: number | null;
    opening?: number;
    openingName: string;

    product: number;

    name:             string;
    productCode:      string;
    parentProductCode:      string;
    finishCode:       string;

    dimensions:       string;
    perLengthInventoryIncrement: string | null;
    perLengthPurchaseIncrement: string;

    isGroup: boolean;
    baseProduct?: number;

    warnReorder: boolean;
    warnLow: boolean;

    manufacturer: number;
    manufacturerName: string;

    belowMinSince?: DateString;

    frame: FrameSpec | null;

    projects: {
        projectId: number;
        projectName: string;
        projectNumber: number;
        inventoryRequestId: number; // pick a random inventory request
        qty: number;
    }[]
}

export type RelabelRelatedRow = {
    inventoryRequest: number;
    projectName: string | null;
    projectNumber: number | null;
    product: number;
    productCode: string;
    manufacturerName: string;
    qty: number;
    opening: string | null;
    isStocking: boolean;
}

export type ToSourceItem = {
    id: number;

    qty: number;
    stockMinQty: number | null;
    idealQty: number | null;
    stockQty: number | null;
    reservedQty: number | null;
    pendingPoQty: number | null;
    purchasedQty: number | null;

    kind: string;

    projectMeta: {
        project: number;
        projectName: string;
        projectNumber: number;
        qty: number;
        neededBy: DateString;
    }[]

    source: string;

    product: number;
    categoryName: string;
    name: string;
    description: string;
    productCode: string;
    finish: string
    dimensions: string;
    dimWidth: string;
    dimHeight: string;
    dimLength: string;
    partOfFrame: boolean;

    frameAnchorName?: string;
    frameAnchorDetail?: ToSourceItem[];

    keyingDetail?: string | null;

    manufacturer: number;
    manufacturerName: string;

    supplier: number;
    supplierName: string;

    project?: number;
    projectName?: string;
    projectNumber?: number;

    openingId: number;
    openingFloor: string | null;
    openingPhase: string | null;
    openingName: string|null;
    openingHardwareGroup: number|null;

    neededBy: DateString | null;
    releasedBy: number | null;

    inventoryRequestId: number | null;
    lastPrice: Cents | null;
    lastPriceAtOthers: Cents | null;

    linkedMiscPrice: number | null;
    linkedProjectWorkOrderItem: number | null;

    doorSeries: string|null;
    doorMaterial: string|null;
    doorFinish: string|null;
    doorGauge: string|null;
    doorElevation: string|null;
    doorCore: string|null;
    doorSeam: string|null;
    doorHanding: string|null;
    doorLabel: string|null;
    doorThickness: string|null;
    doorPreps: Preps | null;

    frameSeries: string|null;
    frameMaterial: string|null;
    frameGauge: string|null;
    frameScreenElev: string|null;
    frameProfile: string|null;
    frameJambDepth: string|null;
    frameDoorThickness: string|null;
    frameConstruction: string|null;
    frameHanding: string|null;
    frameHardwarePreps: string|null;
    frameLabel: string|null;
    frameType: string|null;
    framePreps: Preps | null;

    glassType:               string | null;
    glassThermal:            boolean | null;
    glassThickness:          string | null;
    glassBead:               string | null;
    glassWidth:              string | null;
    glassHeight:             string | null;

    glazingNote:             string | null;
    glazingCutOutWidth:      string | null;
    glazingCutOutHeight:     string | null;

    div10Category:           Div10Category | null | "";
    div10Room:               string;
    div10Product:            number;
    div10Note:               string;
    div10PartitionBracing:   string;
    div10PartitionCoating:   string;
    div10LockerTiers:        string;
    div10LockerSloped:       boolean;
    div10LockerEndPanels:    boolean;
    div10LockerBaseTrim:     boolean;
    div10LockerCoating:      string;
    div10MailboxLoading:     string;
    div10MailboxMounting:    string;
    div10MailboxTenantUnits: number;
    div10MailboxParcelUnits: number;
    div10CornerGuardType:    string;
    div10CornerGuardFinish:  string;
    div10Description:        string;
}

export type PurchaseOrderItem = {
    id: number;
    purchaseOrder: number;
    reference: string;
    description: string;
    code: string;
    qty: number;

    qtyOutstanding: number;
    qtyBackOrdered: number;
    qtyPendingReceiving: number;

    logisticsQtyOutstanding: number;
    unitPriceCents: number;
    archived: boolean;
    updatedAt: DateString;
    product?: number;

    details?: DescriptionInput & {
        div10Room: string | null
    };

    kind: InventoryKind; // will never be frame-anchor b/c frame & it's frame-anchor are combined

    doorSeries?: string | null;
    doorMaterial?: string | null;
    doorFinish?: string | null;
    doorGauge?: string | null;
    doorElevation?: string | null;
    doorLiteKitProfileFile?: number | null;
    doorCore?: string | null;
    doorSeam?: string | null;
    doorHanding?: string | null;
    doorLabel?: string | null;
    doorThickness?: string|null;
    doorWoodDetail?: WoodDetail;
    doorPreps: PrepsEnriched

    frameSeries?: string | null;
    frameMaterial?: string | null;
    frameGauge?: string | null;
    frameScreenElev?: string | null;
    frameProfile?: string | null;
    frameJambDepth?: string | null;
    frameDoorThickness?: string | null;
    frameConstruction?: string | null;
    frameHanding?: string | null;
    frameLabel?: string | null;
    frameType?: string | null;
    framePreps: PrepsEnriched

    dimWidth?: string | null;
    dimHeight?: string | null;
    dimLength?: string | null;

    hwKeyed?: boolean | null; // applies to hardware item
    hwPrepCutFile?: number|null;
    screenElevationFile?: number|null;
    doorElevationFile?: number|null;
    hwPrepGroupNumbers?: number[];
    inventoryRequests?: number[];
    openingIds?: number[];
    openingName?: string | null;
    openingFloor?: string | null;
    openingPhase?: string | null;

    openings?: {
        id: number;
        name: string;
        seqNumber: number;
        locationTransition: string; // to / from
        qty: number;
        type: string; // single/pair...
        handing: string;
        frameProfile: string;
        nominalHeight: string;
        nominalWidth: string;
        subType?: string; // active/inactive (for the purchase order item linked, not the whole opening)
        frameHardwarePreps: PrepsEnriched;
        doorHardwarePreps: PrepsEnriched;
        kind: InventoryKind;
        frameAnchorProductCode?: string | null;
        frameAnchorQtyOverride?: number | null
        hardwareGroup: number;
    }[];

    // one entry per generated glazing row on the item: the elevation variant it was measured on
    // and the marks it covers.
    glazingLabels?: GlazingItemLabel[];

    createStockingInventoryRequestForProduct?: number;

    linkedMiscPrice?: number | null;
    linkedProjectWorkOrderItem?: number | null;
}

export type InventoryKind = 'hardware'|'door'|'frame'|'div-10'|'frame-anchor'|"component"|"glazing";

export type PurchaseOrder = {
    id: number;
    poNumber: string;
    payableRefNumber: string;
    submittedAt: DateString | null;
    targetPickupDate: DateString | null;
    neededBy: DateString | null;
    neededByHasMultiple: boolean;
    status: string;
    payableStatus: string;
    supplier: number;
    supplierName: string;
    supplierAddress: string;
    supplierCity: string;
    supplierProvince: string;
    supplierPostal: string;
    supplierSellsFrames: boolean;
    supplierSellsDoors: boolean;
    supplierSellsHardware: boolean;
    supplierSellsDiv10: boolean;
    deliveryMethod: string;
    totalCents: number;
    updatedAt: DateString;
    updatedBy: number;
    updatedByName: string;
    notes: string;
    dateNotes: string;
    weldShopNotes: string;
    vendorInstructions: string;
    archived: boolean;
    supplierIsTykelWeldShop?: boolean;
    
    linkedProjects?: PoLinkedProject[] | null;
    linkedPayables?: {vendorRef: string; id: number}[] | null;
};

export type GlazingItemLabel = {
    elevation: string;
    label: string | null;
    panels: {mark: string; alignment: string}[];
    qty: number;
};

export type PoLinkedProject = {name: string; id: number, needsRecalculateProducts: boolean; }
export type InvSource = 'non-stocking'| 'purchase'
export type InventoryRequest = {
    id: number;

    // opening
    opening: number;
    openingName: string;
    openingArchived: boolean;
    seqNumber: number | null;
    floor: string | null;
    phase: string | null;
    locationOne: string;
    locationTransition: string;
    locationTwo: string;
    exterior: boolean;
    openingType: string | null;
    nominalWidth: string | null;
    inactiveDoorWidth: string | null;
    nominalHeight: string | null;
    handing: string | null;
    frameAnchorProductName: string | null;

    keyingType: "by-others" | "on-site" | "factory-key" | null;

    // div 10
    div10Item: number | null;
    div10: Div10Spec | null;
    glass?: GlassSpec;
    room: string;
    category: string;
    manufacturer: number;
    manufacturerName: string;
    product: number;
    productCode: string;
    name: string;
    finish: string;
    dimensions: string;
    dimWidth: string;
    dimHeight: string;
    dimLength: string;

    categoryName: string
    productName: string
    productCategoryName?: string

    frame: FrameSpec | null;
    door: DoorSpec | null;

    isStocking: boolean;
    isNonStocking: boolean;
    hasEnoughStock: boolean;
    overrideSource?: InvSource
    isWaste: boolean;
    keyed: boolean;

    reportQuotedPrice: number | null;
    reportPurchasedPrice: number | null;
    reportPayablePrice: number | null;

    qty: number;
    receivedQty?: number;
    followupRequest?: number;
    status: string;
    deliveredAt: string | null;
    updatedBy: string;
    updatedByName: string;
    updatedAt: DateString;
    neededBy: string;
    neededByCalcManuallySet: string | null;
    neededByCalcTimeline: string | null;
    neededByCalcReleaseSet: string | null;

    inventoryItem?: number;

    hardwareGroup?: string;

    type: InventoryKind;

    price: PriceLine2;
    priceBackup: PriceLine2 | null;

    reviewStatus?: ProductReviewStatus
}